/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Chip, CircularProgress, Fab, IconButton, InputBase, Paper, Tooltip, Typography } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getCookie } from "utils/Communicator";
import "./OrderAssistant.scss";

const HEBREW = /[\u0590-\u05FF]/;
const REPORT_TTL_MS = 30 * 60 * 1000; // matches server-side report retention
const RESUME_WITHIN_MS = 12 * 60 * 60 * 1000; // reopen the last conversation if used in the last 12h

const SUGGESTIONS = {
    customer: [
        "What is the status of my orders from the US?",
        "Which shipments are arriving this week?",
        "What was the average transit time of my sea shipments last year?",
        "Export all my active orders to Excel",
        "מה הסטטוס של המשלוחים שלי?",
    ],
    staff: [
        { label: "Full story of a job…", fill: "Give me the full story of job " },
        { label: "Delayed shipments of a customer…", fill: "Which shipments of customer  are delayed?" },
        { label: "Trace all activity on a job…", fill: "Trace all activity on job  — chats, changes and tracking" },
        "Sea shipments that arrived but are not released by customs",
        { label: "Excel report of a customer's active orders…", fill: "Export to Excel all active orders of customer " },
    ],
};

const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const dirOf = (text) => (HEBREW.test(text || "") ? "rtl" : "ltr");

const api = (method, url, data) => {
    const token = getCookie("Authorization");
    return axios.request({ method, url, data, headers: token ? { Authorization: token } : {} });
};

const errorText = (err) => {
    const status = err?.response?.status;
    if (status === 401) return null; // handled by redirect
    if (status === 429) return "Too many questions in a short time. Please wait a few minutes.";
    return err?.response?.data?.error || "The assistant is unavailable right now. Please try again later.";
};

const when = (ms) => {
    const d = new Date(ms);
    const sameDay = new Date().toDateString() === d.toDateString();
    return sameDay
        ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const OPEN_EVENT = "tcl:open-order-assistant";

/**
 * Opens the assistant panel from anywhere in the app, e.g. a header or toolbar button:
 *   import { openOrderAssistant } from 'components/customer/assistant/OrderAssistant';
 *   <Button onClick={() => openOrderAssistant()}>Ask AI</Button>
 * Pass text to pre-fill the question box: openOrderAssistant("Give me the full story of job 110453").
 */
export const openOrderAssistant = (question) => {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { question } }));
};

const OrderAssistant = () => {
    const [enabled, setEnabled] = useState(false);
    const [audience, setAudience] = useState("customer");
    const [open, setOpen] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const listRef = useRef(null);
    const inputRef = useRef(null);
    const mobile = isMobile();

    useEffect(() => {
        let alive = true;
        api("GET", "/api/assistant/status")
            .then(async (res) => {
                if (!alive) return;
                const on = Boolean(res.data?.enabled);
                setEnabled(on);
                if (res.data?.audience) setAudience(res.data.audience);
                if (!on) return;
                // Resume the latest conversation if it is recent; otherwise start fresh.
                const list = (await api("GET", "/api/assistant/conversations")).data || [];
                if (!alive) return;
                setConversations(list);
                if (list[0] && Date.now() - list[0].updatedAt < RESUME_WITHIN_MS) {
                    const conv = (await api("GET", `/api/assistant/conversations/${list[0].id}`)).data;
                    if (alive && conv) {
                        setConversationId(conv.id);
                        setMessages(conv.messages || []);
                    }
                }
            })
            .catch(() => { /* keep whatever state we reached; the button hides if status failed */ });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, loading, open]);

    useEffect(() => {
        if (open && !mobile && inputRef.current) inputRef.current.focus();
    }, [open, mobile]);

    // Opened from elsewhere in the app via openOrderAssistant()
    useEffect(() => {
        const onOpen = (e) => {
            setOpen(true);
            setShowHistory(false);
            const q = e.detail && e.detail.question;
            if (q) setInput(String(q).slice(0, 2000));
        };
        window.addEventListener(OPEN_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_EVENT, onOpen);
    }, []);

    if (!enabled) return null;

    const send = async (text) => {
        const question = (text ?? input).trim();
        if (!question || loading) return;
        setMessages((m) => [...m, { role: "user", content: question, at: Date.now() }]);
        setInput("");
        setError(null);
        setLoading(true);
        try {
            const res = await api("POST", "/api/assistant/orders", { conversationId, message: question });
            const files = Array.isArray(res.data?.files) ? res.data.files : [];
            setMessages((m) => [...m, { role: "assistant", content: res.data?.reply || "…", files, at: Date.now() }]);
            if (res.data?.conversationId && res.data.conversationId !== conversationId) {
                setConversationId(res.data.conversationId);
                setConversations((list) => [{ id: res.data.conversationId, title: res.data.title, updatedAt: Date.now() },
                    ...list.filter((c) => c.id !== res.data.conversationId)]);
            }
        } catch (err) {
            if (err?.response?.status === 401) {
                window.location = "/login";
                return;
            }
            setError(errorText(err));
            // drop the unanswered question so history stays user/assistant alternating
            setMessages((m) => m.slice(0, -1));
            setInput(question);
        } finally {
            setLoading(false);
        }
    };

    // Templates that need a job number or customer go into the input instead of being sent.
    const fillInput = (text) => {
        setInput(text);
        if (inputRef.current) inputRef.current.focus();
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const reset = () => {
        setConversationId(null);
        setMessages([]);
        setError(null);
        setInput("");
        setShowHistory(false);
    };

    const openHistory = async () => {
        setShowHistory(true);
        setError(null);
        try {
            setConversations((await api("GET", "/api/assistant/conversations")).data || []);
        } catch (err) {
            setError(errorText(err));
        }
    };

    const loadConversation = async (id) => {
        setError(null);
        try {
            const conv = (await api("GET", `/api/assistant/conversations/${id}`)).data;
            setConversationId(conv.id);
            setMessages(conv.messages || []);
            setShowHistory(false);
        } catch (err) {
            setError(errorText(err));
        }
    };

    const deleteConversation = async (id) => {
        try {
            await api("DELETE", `/api/assistant/conversations/${id}`);
            setConversations((list) => list.filter((c) => c.id !== id));
            if (id === conversationId) {
                setConversationId(null);
                setMessages([]);
            }
        } catch (err) {
            setError(errorText(err));
        }
    };

    return (
        <>
            {!open && (
                <Tooltip title="Ask about your shipments" placement="left">
                    <Fab className="order-assistant__fab" aria-label="Open shipment assistant" onClick={() => setOpen(true)}>
                        <SmartToyOutlinedIcon />
                    </Fab>
                </Tooltip>
            )}

            {open && (
                <Paper elevation={8} className={`order-assistant__panel${mobile ? " order-assistant__panel--mobile" : ""}`}
                    role="dialog" aria-label="Shipment assistant">
                    <Box className="order-assistant__header">
                        <SmartToyOutlinedIcon fontSize="small" />
                        <Typography className="order-assistant__title">
                            Shipment assistant{audience === "staff" ? " · internal" : ""}
                        </Typography>
                        {showHistory ? (
                            <Tooltip title="Back to chat">
                                <IconButton size="small" onClick={() => setShowHistory(false)} aria-label="Back to chat">
                                    <ArrowBackIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Previous conversations">
                                <span>
                                    <IconButton size="small" onClick={openHistory} disabled={loading} aria-label="Previous conversations">
                                        <HistoryIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="New conversation">
                            <span>
                                <IconButton size="small" onClick={reset} disabled={loading || (messages.length === 0 && !showHistory)} aria-label="New conversation">
                                    <RestartAltIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {showHistory && (
                        <Box className="order-assistant__history">
                            {conversations.length === 0 && (
                                <Typography className="order-assistant__hint">No previous conversations yet.</Typography>
                            )}
                            {conversations.map((c) => (
                                <Box key={c.id} className={`order-assistant__history-item${c.id === conversationId ? " is-current" : ""}`}>
                                    <button type="button" className="order-assistant__history-open" onClick={() => loadConversation(c.id)}>
                                        <span className="order-assistant__history-title" dir={dirOf(c.title)}>{c.title}</span>
                                        <span className="order-assistant__history-when">{when(c.updatedAt)}</span>
                                    </button>
                                    <IconButton size="small" onClick={() => deleteConversation(c.id)} aria-label="Delete conversation">
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box className="order-assistant__messages" ref={listRef} style={showHistory ? { display: "none" } : undefined}>
                        {messages.length === 0 && (
                            <Box className="order-assistant__empty">
                                <Typography className="order-assistant__hint">
                                    {audience === "staff"
                                        ? "Ask about any shipment — status, full history with chats and changes, or customer-wide questions."
                                        : "Ask me about your shipments — status, ETA, full history, or a specific job number."}
                                </Typography>
                                <Box className="order-assistant__suggestions">
                                    {(SUGGESTIONS[audience] || SUGGESTIONS.customer).map((s) => {
                                        const label = typeof s === "string" ? s : s.label;
                                        return (
                                            <Chip key={label} label={label} dir={dirOf(label)} variant="outlined" size="small"
                                                onClick={() => (typeof s === "string" ? send(s) : fillInput(s.fill))} />
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        {messages.map((m, i) => (
                            <React.Fragment key={i}>
                                <Box dir={dirOf(m.content)}
                                    className={`order-assistant__bubble order-assistant__bubble--${m.role}`}>
                                    {m.content}
                                </Box>
                                {(m.files || []).map((f) => {
                                    const expired = m.at && Date.now() - m.at > REPORT_TTL_MS;
                                    return expired ? (
                                        <Box key={f.url} className="order-assistant__file order-assistant__file--expired">
                                            {f.name} — link expired, ask again to regenerate
                                        </Box>
                                    ) : (
                                        <a key={f.url} className="order-assistant__file" href={f.url} download={f.name}>
                                            <FileDownloadOutlinedIcon fontSize="small" />
                                            <span className="order-assistant__file-name">{f.name}</span>
                                            <span className="order-assistant__file-rows">{f.rows} rows</span>
                                        </a>
                                    );
                                })}
                            </React.Fragment>
                        ))}

                        {loading && (
                            <Box className="order-assistant__bubble order-assistant__bubble--assistant order-assistant__typing">
                                <CircularProgress size={14} thickness={5} /> Checking your shipments…
                            </Box>
                        )}
                    </Box>

                    {error && <Box className="order-assistant__error" role="alert">{error}</Box>}

                    <Box className="order-assistant__composer" style={showHistory ? { display: "none" } : undefined}>
                        <InputBase
                            inputRef={inputRef}
                            className="order-assistant__input"
                            placeholder="Ask about your shipments…"
                            value={input}
                            onChange={(e) => setInput(e.target.value.slice(0, 2000))}
                            onKeyDown={onKeyDown}
                            multiline
                            maxRows={4}
                            dir={dirOf(input)}
                            disabled={loading}
                            inputProps={{ "aria-label": "Your question" }}
                        />
                        <IconButton className="order-assistant__send" onClick={() => send()} disabled={loading || !input.trim()} aria-label="Send">
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Typography className="order-assistant__disclaimer">
                        AI answers can be wrong — check the shipment page for official details.
                    </Typography>
                </Paper>
            )}
        </>
    );
};

export default OrderAssistant;
